import * as React from 'react';

import { animated, useSpring, config } from 'react-spring';

import { Colors } from 'theme/colors';
import { ACTIVE_OPACITY } from 'utils/constants';
import { Medium } from 'theme/text';

import { SwitchButton, SwitchContainer, SWITCH_WIDTH, KNOB_WIDTH, Overlay, Knob } from './styles';

const PADDING = 5; // A sum of the padding on both horizontal sides
const TRANSLATE_X = SWITCH_WIDTH - KNOB_WIDTH - PADDING;
const SwitchOverlay = animated(Overlay);
const SwitchKnob = animated(Knob);

/**
 * Toggle Switch for representing a boolean value.
 *
 * @param props - Render props
 */
export function Switch(props: Props) {
  const { label, name, onToggle, value, accentColor } = props;
  const [isOn, setSwitch] = React.useState(value);
  const { factor, translation } = useSpring({
    translation: `translateX(${isOn ? TRANSLATE_X : 0}px)`,
    factor: isOn ? 1 : 0,
    config: config.gentle,
  });

  /** Flip the switch on press */
  const handleOnPress = React.useCallback(() => {
    const nextState = !isOn;

    if (onToggle) {
      onToggle({ [name]: nextState });
    }

    setSwitch(nextState);
  }, [isOn, name, onToggle]);

  React.useEffect(() => {
    /* Synchronise switch on mount and when props update. */
    setSwitch(value);
  }, [value, setSwitch]);
  const opacity = factor.interpolate((o: number) => 1 - o);
  const scale = factor.interpolate((o: number) => `scale(${1 - o})`);

  return (
    <SwitchContainer>
      <Medium>{label}</Medium>
      <SwitchButton
        backgroundColor={accentColor as string}
        activeOpacity={ACTIVE_OPACITY}
        onPress={handleOnPress}>
        <SwitchOverlay
          as="div"
          style={{
            opacity,
            transform: scale,
          }}
        />
        <SwitchKnob as="div" style={{ transform: translation }} />
      </SwitchButton>
    </SwitchContainer>
  );
}
/* Type definitions */
interface Props {
  accentColor: Colors;
  /** Text to display on the switch control. */
  label: string;
  /** Name of the property bound to the value. */
  name: string;
  /** A flag to indicate the current state of the switch control. */
  value: boolean;
  /** Emits an on value change event when the switch state changes.  */
  onToggle?(value: Dictionary<boolean>): void;
}
